import { spatialReferenceSystems } from '@skand/data-3d-loader';
import { Ellipsoid } from '@skand/three-js-renderer/bin/private/math';
import earcut from 'earcut';
import proj4 from 'proj4';
import { Matrix3, Matrix4, Quaternion, Sphere, Vector2, Vector3 } from 'three';
/**
 * Convert an angle from radians to degrees.
 *
 * @param radians
 * @returns
 */
export function toDegrees(radians) {
    return (radians * 180) / Math.PI;
}
/**
 * Convert an angle from degrees to radians.
 *
 * @param degrees
 * @returns
 */
export function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
}
/**
 * Convert a cartesian position to cartographic.
 *
 * @param position
 * @returns
 */
export function toCartographic(position) {
    return Ellipsoid.WGS_84.cartographicForPosition(position);
}
/**
 * Convert a cartographic position to cartesian.
 *
 * @param position
 * @returns
 */
export function toCartesian(position) {
    return Ellipsoid.WGS_84.cartesianForCartographic(position);
}
/**
 * Calculate the rotation basis for a given cartographic position.
 *
 * @param position
 * @param normal    Normal result
 * @param tangent   Tangent result
 * @param bitangent Bitangent result
 */
export function calculateCartographicBasis(position, normal, tangent, bitangent) {
    Ellipsoid.WGS_84.rotationBasisForCartographic(position, null, normal, tangent, bitangent);
}
/**
 * Calculate the rotation basis at the given cartographic position. "Up" will be defined as
 * the surface normal of the underlying ellipsoid at that position.
 *
 * @param cartographic
 */
export function calculateCartographicQuaternion(cartographic) {
    const matrix3 = new Matrix3();
    Ellipsoid.WGS_84.rotationBasisForCartographic(cartographic, matrix3);
    const R = new Matrix4().setFromMatrix3(matrix3);
    return new Quaternion().setFromRotationMatrix(R);
}
/**
 * Convert a point in ECEF to a projected spatial reference system in easting, northing, and geoidal height.
 *
 * @param point
 * @param epsg
 * @returns
 */
export function toProjectedCoordinates(point, epsg) {
    proj4.defs(epsg, spatialReferenceSystems[epsg].proj4);
    const cartograhpic = toCartographic(point);
    const coordinates = new Vector3(toDegrees(cartograhpic.longitude), toDegrees(cartograhpic.latitude), cartograhpic.height);
    const { x, y, z } = proj4('EPSG:4326', epsg).forward(coordinates);
    return new Vector3(x, y, z);
}
/**
 * Convert a point in a spatial reference system to ECEF.
 *
 * @param point
 * @param epsg
 * @returns
 */
export function toECEF(point, epsg) {
    const ecef = 'EPSG:4978';
    proj4.defs(epsg, spatialReferenceSystems[epsg].proj4);
    proj4.defs(ecef, spatialReferenceSystems[ecef].proj4);
    const { x, y, z } = proj4(epsg, ecef).forward(point);
    return new Vector3(x, y, z);
}
/**
 * Calculate the horizontal and vertical lengths between two points.
 *
 * @param a
 * @param b
 * @returns
 */
export function calculateHVLengths(a, b) {
    const hv = new Vector2();
    const midpoint = a.clone().add(b).divideScalar(2);
    const surfaceNormal = new Vector3();
    const surfaceTangent = new Vector3();
    calculateCartographicBasis(toCartographic(midpoint), surfaceNormal, surfaceTangent, new Vector3());
    // Calculate the horizontal length by projecting onto a plane on the globe surface
    const aHor = a.clone().projectOnPlane(surfaceNormal);
    const bHor = b.clone().projectOnPlane(surfaceNormal);
    hv.x = aHor.distanceTo(bHor);
    // Calculate the vertical length by projecting onto a plane perpendicular to globe surface
    const aVer = a.clone().distanceTo(aHor);
    const bVer = b.clone().distanceTo(bHor);
    hv.y = Math.abs(aVer - bVer);
    return hv;
}
/**
 * Calculate the angle between two line segments formed by the three points (BA-BC) in degrees.
 *
 * @param a
 * @param b
 * @param c
 * @returns
 */
export function calculateAngle(a, b, c) {
    const ba = a.clone().sub(b);
    const bc = c.clone().sub(b);
    return toDegrees(ba.angleTo(bc));
}
/**
 * Calculate the absolute height difference between two points on the ellipsoid.
 *
 * @param a
 * @param b
 * @returns
 */
export function calculateElevation(a, b) {
    const a_c = toCartographic(a);
    const b_c = toCartographic(b);
    // Round it to two decimal places
    return Math.abs(a_c.height - b_c.height);
}
/**
 * Calculate the lengths of each edge in a vertex array.
 *
 * @param vertices Ordered list of vertices
 * @param closed   Is this a closed polygon or a set of lines?
 * @returns
 */
export function calculateEdgeLengths(vertices, closed) {
    const lengths = [];
    const positions = vertices.slice();
    if (closed) {
        positions.push(positions[0]);
    }
    for (let i = 0; i < positions.length - 1; i++) {
        lengths.push(positions[i].distanceTo(positions[i + 1]));
    }
    return lengths;
}
/**
 * Calculate the lengths of each edge in a vertex array.
 *
 * @param vertices Ordered list of vertices
 * @param closed   Is this a closed polygon or a set of lines?
 * @returns
 */
export function calculateEdgeHVLengths(vertices, closed) {
    const lengths = [];
    const positions = vertices.slice();
    if (closed) {
        positions.push(positions[0]);
    }
    for (let i = 0; i < positions.length - 1; i++) {
        lengths.push(calculateHVLengths(positions[i], positions[i + 1]));
    }
    return lengths;
}
/**
 * Calculate the angle formed between each pair of lines given a list of vertices in degrees. Each point is mapped to an angle, so if not closed, begins and ends with 0.
 *
 * @param vertices Ordered list of vertices
 * @param closed   Is this a closed polygon or a set of lines?
 * @returns
 */
export function calculateCornerAngles(vertices, closed) {
    const angles = [];
    const positions = vertices.slice();
    if (closed) {
        const first = positions[0];
        const last = positions[positions.length - 1];
        positions.unshift(last);
        positions.push(first);
    }
    for (let i = 0; i < positions.length - 2; i++) {
        angles.push(calculateAngle(positions[i], positions[i + 1], positions[i + 2]));
    }
    return closed ? angles : [0, ...angles, 0];
}
/**
 * Calculate the elevation difference between the first vertex and every other vertex.
 *
 * @param vertices Ordered list of vertices
 * @returns
 */
export function calculateVertexElevations(vertices) {
    const elevations = [];
    for (let i = 0; i < vertices.length; i++) {
        elevations.push(calculateElevation(vertices[0], vertices[i]));
    }
    return elevations;
}
/**
 * Given a list of vertices of a 3D polyhedron, a triangle mesh can be generated by determining the list of indices.
 *
 * @param vertices Unordered list of vertices
 * @returns
 */
export function triangulate(vertices) {
    const flatVertices = [];
    for (const vertex of vertices) {
        flatVertices.push(vertex.x);
        flatVertices.push(vertex.y);
        flatVertices.push(vertex.z);
    }
    const indices = earcut(flatVertices, [], 3);
    for (let i = 0; i < indices.length - 2; i += 3) {
        // Sort each triangle index triple in descending order
        const triple = [indices[i], indices[i + 1], indices[i + 2]];
        triple.sort((a, b) => b - a);
        indices[i] = triple[0];
        indices[i + 1] = triple[1];
        indices[i + 2] = triple[2];
    }
    return indices;
}
/**
 * Calculate the area of a triangle defined by three vertices.
 *
 * @param a
 * @param b
 * @param c
 * @returns
 */
export function calculateTriangleArea(a, b, c) {
    const ab = b.clone().sub(a);
    const ac = c.clone().sub(a);
    const cross = ab.cross(ac);
    return cross.length() / 2.0;
}
/**
 * Calculate the area of the polygon formed by the vertex list.
 *
 * @param vertices Ordered list of vertices
 */
export function calculatePolygonArea(vertices) {
    const indices = triangulate(vertices);
    let area = 0.0;
    for (let i = 0; i < indices.length - 2; i += 3) {
        // Calculate the area of the triangle formed by the three vertices
        const a = vertices[indices[i]];
        const b = vertices[indices[i + 1]];
        const c = vertices[indices[i + 2]];
        area += calculateTriangleArea(a, b, c);
    }
    return area;
}
/**
 * Calculate the centroid of a point cluster.
 *
 * @param points
 */
export function calculateCentroid(points) {
    const centroid = new Vector3();
    for (const point of points) {
        centroid.add(point);
    }
    centroid.divideScalar(points.length);
    return centroid;
}
/**
 * Calculate the target position to fit an object, defined by its center and size, to camera view.
 *
 * This assumes the object is at or above surface level.
 *
 * @param center Object position
 * @param size   Size of the object
 * @param fov    Camera field of view
 * @returns
 */
export function calculateCameraTargetPosition(center, size, fov) {
    const heightOffset = size / Math.tan(fov / 2);
    const cartographic = toCartographic(center);
    cartographic.height += heightOffset;
    return toCartesian(cartographic);
}
/**
 * Calculate the quaternion for a bird's-eye view orientation towards a
 * position on the globe
 *
 * @param position Position in ECEF
 * @returns
 */
export function calculateNadirOrientation(position) {
    const cartographic = toCartographic(position);
    const x = new Vector3();
    const y = new Vector3();
    const z = new Vector3();
    // Z (forward) is the negated normal
    // Y (up) is parallel to the longitude line
    // X (right) is parallel to the latitude line
    calculateCartographicBasis(cartographic, z, y, x);
    const R = new Matrix4().makeBasis(x, y, z);
    return new Quaternion().setFromRotationMatrix(R);
}
/**
 * Project a point from world space to screen space, the inverse operation to IRenderer.pickScreenWorldSpace()
 *
 * @param camera      Camera
 * @param dimensions  Dimensions of the screen
 * @param point       Point in world space
 */
export function projectToScreen(camera, dimensions, point) {
    const view = new Matrix4();
    camera.getWorldMatrix(view);
    view.invert();
    const projection = new Matrix4();
    camera.getProjectionMatrix(projection);
    const transform = new Matrix4();
    transform.multiplyMatrices(projection, view);
    const result = point.clone().applyMatrix4(transform);
    return new Vector2((dimensions.x * (result.x + 1)) / 2.0, dimensions.y * (1 - (result.y + 1) / 2.0));
}
/**
 * Test if a position in world space is visible to the camera
 *
 * @param camera Camera
 * @param point  Point in world space
 * @returns
 */
export function isCameraVisible(camera, point) {
    const cameraPosition = new Vector3();
    camera.getPositionECEF(cameraPosition);
    const localMatrix = new Matrix4();
    camera.getLocalMatrix(localMatrix);
    const back = new Vector3();
    localMatrix.extractBasis(new Vector3(), new Vector3(), back);
    back.negate();
    const relativePosition = point.clone().sub(cameraPosition);
    return back.dot(relativePosition) > 1;
}
/**
 * Compute the sphere that contains all other spheres.
 *
 * @param spheres
 * @returns
 */
export function computeContainerSphere(spheres) {
    const centroid = new Vector3();
    for (const sphere of spheres) {
        centroid.add(sphere.center);
    }
    centroid.divideScalar(spheres.length);
    let radius = 0;
    for (const sphere of spheres) {
        const distance = centroid.distanceTo(sphere.center) + sphere.radius;
        radius = Math.max(radius, distance);
    }
    return new Sphere(centroid, radius);
}
/**
 * Compute the pick position slightly offset from the surface to prevent z-fighting.
 *
 * @param renderer
 * @param screenPosition
 * @returns
 */
export function computePick(renderer, screenPosition) {
    // Pick position
    const pick = renderer.pickScreenWorldSpaceSnapping(screenPosition);
    const position = new Vector3(pick.position.x, pick.position.y, pick.position.z);
    // Pick surrounding points
    const PICK_OFFSETS = [new Vector2(1, 0), new Vector2(-1, 0), new Vector2(0, 1)];
    const points = [new Vector3(), new Vector3(), new Vector3()];
    for (let i = 0; i < 3; i++) {
        const target = screenPosition.clone().add(PICK_OFFSETS[i]);
        const pickOffset = renderer.pickScreenWorldSpace(target);
        points[i].set(pickOffset.position.x, pickOffset.position.y, pickOffset.position.z);
    }
    // Compute normal
    const [a, b, c] = points;
    const ab = b.sub(a);
    const ac = c.sub(a);
    const normal = ab.cross(ac).normalize();
    return position.add(normal.multiplyScalar(0.015));
}
